import axios, { AxiosInstance } from 'axios';
import { LoginCredentials, RegisterData, User, Report, AuditLog, GIC, GicMaturityAlerts, GicSummary, GicAnalysis } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3035/api';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      if (status === 401) {
        localStorage.removeItem('authToken');
        return Promise.reject(new Error('Invalid email or password'));
      }
      if (status === 403) return Promise.reject(new Error('Access denied. Insufficient permissions.'));
      if (status === 404) return Promise.reject(new Error('Resource not found.'));
      if (status >= 500) return Promise.reject(new Error('Server error. Please try again later.'));
      if (data?.error) return Promise.reject(new Error(data.error));
    }
    if (error.message?.includes('Network Error')) {
      return Promise.reject(new Error('Cannot connect to server. Please check if backend is running.'));
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: (credentials: LoginCredentials) => api.post<{ token: string; user: User }>('/auth/login', credentials),
  register: (userData: RegisterData) => api.post<{ token: string; user: User }>('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  checkAuth: () => api.get<User>('/auth/check'),
  changePassword: (data: { current_password: string; new_password: string; confirm_password: string }) =>
    api.post('/auth/change-password', data),
  updateProfile: (data: { name: string; email: string }) => api.put<User>('/auth/profile', data),
  getProfile: () => api.get<User>('/auth/profile'),
};

export const userService = {
  getAll: (params?: object) => api.get<User[]>('/admin/users', params),
  create: (userData: Partial<User> & { password: string }) => api.post<User>('/admin/users', userData),
  updateUser: (userId: number, data: { name?: string; email?: string; role_id?: number }) =>
    api.put<User>(`/admin/users/${userId}`, data),
  updateStatus: (userId: number, isActive: boolean) =>
    api.put<User>(`/admin/users/${userId}/status`, { is_active: isActive }),
  resetPassword: (userId: number, newPassword: string) =>
    api.post(`/admin/users/${userId}/reset-password`, { new_password: newPassword }),
  delete: (userId: number) => api.delete(`/admin/users/${userId}`),
};

export const reportService = {
  getAll: (params?: object) => api.get<Report[]>('/reports', { params }),
  getById: (id: string | number) => api.get<Report>(`/reports/${id}`),
  create: (data: { category: string; content: unknown }) => api.post<Report>('/reports', data),
  update: (id: string | number, data: object) => api.put<Report>(`/reports/${id}`, data),
  delete: (id: string | number) => api.delete(`/reports/${id}`),
  getCategories: () => api.get<string[]>('/reports/categories'),
};

export const gicService = {
  getAll: (params?: object) => api.get<GIC[]>('/gic', { params }),
  getById: (id: string | number) => api.get<GIC>(`/gic/${id}`),
  create: (data: Partial<GIC>) => api.post<GIC>('/gic', data),
  update: (id: string | number, data: Partial<GIC>) => api.put<GIC>(`/gic/${id}`, data),
  delete: (id: string | number) => api.delete(`/gic/${id}`),
  getBanks: () => api.get<string[]>('/gic/banks'),
  getMaturityAlerts: (windows?: string) => api.get<GicMaturityAlerts>('/gic/alerts/maturity', { params: { windows } }),
  getSummary: () => api.get<GicSummary>('/gic/summary'),
  analyze: (focus?: string) => api.post<GicAnalysis>('/gic/analyze', focus ? { focus } : {}),
  exportCSV: () => api.get('/gic/export/csv', { responseType: 'blob' }),
};

export const auditService = {
  getLogs: (params?: object) => api.get<AuditLog[]>('/audit/logs', { params }),
  getStats: () => api.get('/admin/stats'),
  getRecent: (params?: object) => api.get<AuditLog[]>('/audit/recent', { params }),
  getFailedLogins: (params?: object) => api.get<AuditLog[]>('/audit/failed-logins', { params }),
  exportCSV: (startDate: string, endDate: string) =>
    api.get('/audit/export', { params: { start_date: startDate, end_date: endDate }, responseType: 'blob' }),
};

export default api;
